import { useState } from 'react';
import { useCreateUser } from '../hooks/useUser';
import { useRoles } from '../hooks/useRoles';
import { useDepartments } from '@/shared/hooks/useDepartments';
import { useCounties } from '@/shared/hooks/useMetaData';
import { useConfirm } from '@/shared/contexts/confirm-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PhoneNumberInput } from '@/shared/components/PhoneNumberInput';
import { Plus } from 'lucide-react';
import { normalizeApiError } from '@/shared/utils/errorHandler';
import { LoadingAnimation } from '@/shared/components/LoadingAnimation';

const AGRONOMIST_ROLE_NAME = 'Agronomist';

const EMPTY_FORM = {
  email: '',
  password: '',
  firstName: '',
  otherNames: '',
  phoneNumber: '',
  userName: '',
  roleName: '',
  agronomistRegions: [] as string[],
  departmentId: '',
};

export const CreateUserDialog = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);

  const createMutation = useCreateUser();
  const confirm = useConfirm();
  const { data: rolesPage, isLoading: rolesLoading } = useRoles(1, 100);
  const roles = rolesPage?.items;
  const { data: departments = [] } = useDepartments();
  const { data: counties = [], isLoading: countiesLoading } = useCounties();
  const isAgronomistRole = formData.roleName === AGRONOMIST_ROLE_NAME;

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
    if (globalError) setGlobalError(null);
  };

  const handleRegionToggle = (regionName: string, checked: boolean) => {
    setFormData((prev) => {
      const nextRegions = checked
        ? [...prev.agronomistRegions, regionName]
        : prev.agronomistRegions.filter((region) => region !== regionName);

      return {
        ...prev,
        agronomistRegions: Array.from(new Set(nextRegions)).sort((a, b) => a.localeCompare(b)),
      };
    });

    if (fieldErrors.agronomistRegions) {
      setFieldErrors((prev) => {
        const { agronomistRegions: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setGlobalError(null);

    if (isAgronomistRole && formData.agronomistRegions.length === 0) {
      setFieldErrors({ agronomistRegions: ['At least one region is required for Agronomist users.'] });
      return;
    }

    const payload = {
      ...formData,
      phoneNumber: formData.phoneNumber.trim() || undefined,
      agronomistRegions: isAgronomistRole ? formData.agronomistRegions : undefined,
      departmentId: formData.departmentId || null,
    };

    confirm({
      title: "Create user?",
      description: `${`${formData.firstName} ${formData.otherNames}`.trim()} will be added to the system${formData.roleName ? ` with the ${formData.roleName} role` : ""}.`,
      confirmLabel: "Create user",
      onConfirm: () =>
        createMutation
          .mutateAsync(payload)
          .then(() => {
            setFormData(EMPTY_FORM);
            setOpen(false);
          })
          .catch((error: unknown) => {
        const normalized = normalizeApiError(error);
        if (normalized.errors && Object.keys(normalized.errors).length > 0) {
          // Map backend field names to form field names
          const fieldMapping: Record<string, string> = {
            username: 'userName',
            firstname: 'firstName',
            othernames: 'otherNames',
            phonenumber: 'phoneNumber',
            rolename: 'roleName',
            agronomistregions: 'agronomistRegions',
          };

          const mapped: Record<string, string[]> = {};
          const unmappedErrors: string[] = [];

          for (const [field, messages] of Object.entries(normalized.errors)) {
            const formField = fieldMapping[field.toLowerCase()] || field;
            if (fieldMapping[field.toLowerCase()]) {
              mapped[formField] = messages;
            } else {
              // Collect unmapped errors to show as global errors
              unmappedErrors.push(...messages);
            }
          }

          setFieldErrors(mapped);
          if (unmappedErrors.length > 0) {
            setGlobalError(unmappedErrors.join('; '));
          }
        } else {
          setGlobalError(normalized.message);
        }
        throw error;
          }),
    });
  };

  return (
    <>
      {createMutation.isPending && (
        <LoadingAnimation fullScreen className="z-[100]" text="Creating user and sending welcome notification…" />
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            New User
          </Button>
        </DialogTrigger>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
          <DialogDescription>
            Add a new user to the system. A temporary password will be generated if left blank.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {globalError && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {globalError}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field>
              <FieldLabel htmlFor="firstName">First Name</FieldLabel>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                required
                disabled={createMutation.isPending}
              />
              {fieldErrors.firstName && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.firstName.join(', ')}</p>
              )}
            </Field>

            <Field>
              <FieldLabel htmlFor="otherNames">Other Names</FieldLabel>
              <Input
                id="otherNames"
                value={formData.otherNames}
                onChange={(e) => handleChange('otherNames', e.target.value)}
                required
                disabled={createMutation.isPending}
              />
              {fieldErrors.otherNames && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.otherNames.join(', ')}</p>
              )}
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              disabled={createMutation.isPending}
            />
            {fieldErrors.email && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.email.join(', ')}</p>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="userName">Username</FieldLabel>
            <Input
              id="userName"
              value={formData.userName}
              onChange={(e) => handleChange('userName', e.target.value)}
              required
              disabled={createMutation.isPending}
            />
            {fieldErrors.userName && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.userName.join(', ')}</p>
            )}
          </Field>

          <Field>
            <FieldLabel>
              Role <span className="text-red-500">*</span>
            </FieldLabel>
            <Select
              value={formData.roleName}
              onValueChange={(value) => {
                handleChange('roleName', value);
                if (value !== AGRONOMIST_ROLE_NAME) {
                  setFormData((prev) => ({ ...prev, agronomistRegions: [] }));
                }
              }}
              disabled={createMutation.isPending || rolesLoading}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={rolesLoading ? 'Loading roles...' : 'Select a role'}
                />
              </SelectTrigger>
              <SelectContent>
                {roles?.map((role) => (
                  <SelectItem key={role.id} value={role.name}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldErrors.roleName && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.roleName.join(', ')}</p>
            )}
          </Field>

          {isAgronomistRole ? (
            <Field>
              <FieldLabel>
                Regions <span className="text-red-500">*</span>
              </FieldLabel>
              <div className="rounded-md border p-3 space-y-2 max-h-48 overflow-y-auto">
                {countiesLoading ? (
                  <p className="text-sm text-gray-500">Loading regions...</p>
                ) : counties.length === 0 ? (
                  <p className="text-sm text-gray-500">No regions available.</p>
                ) : (
                  counties.map((county) => {
                    const checked = formData.agronomistRegions.includes(county.name);
                    return (
                      <label key={county.code} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(value) => handleRegionToggle(county.name, Boolean(value))}
                          disabled={createMutation.isPending}
                        />
                        <span>{county.name}</span>
                      </label>
                    );
                  })
                )}
              </div>
              {fieldErrors.agronomistRegions && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.agronomistRegions.join(', ')}</p>
              )}
            </Field>
          ) : null}

          <Field>
            <FieldLabel>Department <span className="text-gray-400 font-normal text-xs">(optional)</span></FieldLabel>
            <Select
              value={formData.departmentId}
              onValueChange={(value) => handleChange('departmentId', value === '__none__' ? '' : value)}
              disabled={createMutation.isPending}
            >
              <SelectTrigger>
                <SelectValue placeholder="No department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No department</SelectItem>
                {departments.filter((d) => d.isActive).map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <PhoneNumberInput
            label="Phone Number (optional)"
            required={false}
            value={formData.phoneNumber}
            onChange={(value) => handleChange('phoneNumber', value)}
            disabled={createMutation.isPending}
          />
          {fieldErrors.phoneNumber && (
            <p className="text-red-500 text-xs mt-1">{fieldErrors.phoneNumber.join(', ')}</p>
          )}

          <Field>
            <FieldLabel htmlFor="password">
              Password{' '}
              <span className="text-gray-400 font-normal text-xs">(leave blank to auto-generate)</span>
            </FieldLabel>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              disabled={createMutation.isPending}
              autoComplete="new-password"
            />
            {fieldErrors.password && (
              <p className="text-red-500 text-xs mt-1">{fieldErrors.password.join(', ')}</p>
            )}
          </Field>

          <Button
            type="submit"
            className="w-full"
            disabled={createMutation.isPending || !formData.roleName || (isAgronomistRole && formData.agronomistRegions.length === 0)}
          >
            {createMutation.isPending ? 'Creating...' : 'Create User'}
          </Button>
        </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
